import { useMemo } from "react";
import { useDeliveryAddressesQuery } from "./useDeliveryAddressesQuery";
import { useDeliveryQuestionsQuery } from "./useDeliveryQuestionsQuery";
import { filterLocationsByProgramId } from "./address-utils";
import { usePlansQuery } from "../Plans/usePlansQuery";
/**
 * Orchestrator hook that combines all address-related data from SWR cache.
 *
 * This hook provides a unified interface for accessing:
 * - Delivery addresses (hydrated on login)
 * - Delivery questions (hydrated on login)
 * - Company address (hydrated on login)
 * - Customer payment methods (hydrated on login)
 *
 * ⚠️ PURE DATA FETCHING ONLY - No side effects
 * - Does NOT call useBillingInformation()
 * - Does NOT update CheckoutContext
 * - Side effects are handled by useAddresses wrapper hook
 *
 * @param params - Optional parameters for filtering/context
 * @param params.programId - Filter pickup locations by program ID
 * @param params.orderId - Order ID for context (not used by queries, just passed through)
 * @param params.programSKU - Program SKU (not used - here for API compatibility)
 *
 * @returns Combined address data with loading states
 *
 * @example
 * ```typescript
 * const {
 *   addresses,
 *   pickupLocations,
 *   deliveryQuestions,
 *   loading,
 *   error,
 * } = useAddressesData({ programId: 123 });
 * ```
 */
export const useAddressesData = (params) => {
    const { programId, orderId } = params || {};
    // Fetch all data sources from SWR cache
    const { data: addressesResponse, fetchError: addressesError, isLoading: addressesLoading, } = useDeliveryAddressesQuery();
    const { data: questionsData, fetchError: questionsError, isLoading: questionsLoading } = useDeliveryQuestionsQuery();
    const { data: plansResponse, error: plansError, isLoading: plansLoading } = usePlansQuery();
    // Extract data from responses
    const addresses = (addressesResponse === null || addressesResponse === void 0 ? void 0 : addressesResponse.data) || [];
    const allPickupLocations = (addressesResponse === null || addressesResponse === void 0 ? void 0 : addressesResponse.pickupLocations) || [];
    const deliveryQuestions = questionsData || [];
    // Filter pickup locations by programId if provided
    const filteredPickupLocations = useMemo(() => filterLocationsByProgramId(allPickupLocations, programId), [allPickupLocations, programId]);
    // Combine loading states
    const loading = addressesLoading || questionsLoading || plansLoading;
    const loadingAddresses = addressesLoading;
    const loadingError = Boolean(addressesError || questionsError || plansError);
    // Combine errors (return first error found)
    const error = addressesError || questionsError || plansError;
    // Memoize the return value for performance
    const result = useMemo(() => ({
        addresses,
        pickupLocations: filteredPickupLocations,
        loading,
        loadingAddresses,
        loadingError,
        deliveryQuestions,
        orderId,
        // Defaults - useAddresses wrapper derives actual values from plansResponse
        usesLocation: false,
        usesZipCodes: false,
        error,
        // Convenience properties
        isLoading: loading,
        hasError: loadingError,
        plansResponse,
    }), [
        addresses,
        filteredPickupLocations,
        loading,
        loadingAddresses,
        loadingError,
        deliveryQuestions,
        orderId,
        error,
        plansResponse,
    ]);
    return result;
};
